import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { QuestionCategory, WizardQuestion } from './interviews.types';
import { QuestionCard } from './question-card';

import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

const CATEGORY_COLORS: Record<QuestionCategory, string> = {
  eligibility: 'border-l-[var(--color-section-blue)]',
  job_expectations: 'border-l-[var(--color-section-green)]',
  assessment: 'border-l-[var(--color-section-orange)]',
  information: 'border-l-[var(--color-section-purple)]'
};

type QuestionCategorySectionProps = {
  category: QuestionCategory;
  questions: WizardQuestion[];
  onUpdate: (index: number, updated: WizardQuestion) => void;
  onDelete: (index: number) => void;
  onAdd: () => void;
  onReorder: (activeId: string, overId: string) => void;
};

export function QuestionCategorySection({
  category,
  questions,
  onUpdate,
  onDelete,
  onAdd,
  onReorder
}: QuestionCategorySectionProps): React.ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const sensors = useSensors(useSensor(PointerSensor));

  const categoryLabels: Record<QuestionCategory, string> = {
    eligibility: t('wizard.questions.categories.eligibility'),
    job_expectations: t('wizard.questions.categories.job_expectations'),
    assessment: t('wizard.questions.categories.assessment'),
    information: t('wizard.questions.categories.information')
  };

  const categoryDescriptions: Record<QuestionCategory, string> = {
    eligibility: t('wizard.questions.categoryDescriptions.eligibility'),
    job_expectations: t('wizard.questions.categoryDescriptions.job_expectations'),
    assessment: t('wizard.questions.categoryDescriptions.assessment'),
    information: t('wizard.questions.categoryDescriptions.information')
  };

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-border border-l-4 bg-card shadow-sm',
        CATEGORY_COLORS[category]
      )}
    >
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="flex w-full items-center justify-between p-4"
      >
        <div className="flex flex-col items-start gap-0.5">
          <span className="text-base font-semibold">{categoryLabels[category]}</span>
          <span className="text-xs text-muted-foreground">{categoryDescriptions[category]}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{questions.length}</span>
          <ChevronDown
            className={cn(
              'size-4 text-muted-foreground transition-transform',
              open && 'rotate-180'
            )}
          />
        </div>
      </button>

      {open && (
        <div className="flex flex-col gap-0 border-t border-border px-3 py-2 sm:gap-2 sm:p-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={questions.map((q) => q.id ?? '')}
              strategy={verticalListSortingStrategy}
            >
              {questions.map((q, i) => (
                <QuestionCard
                  key={q.id ?? i}
                  id={q.id ?? ''}
                  question={q}
                  onUpdate={(updated) => {
                    onUpdate(i, updated);
                  }}
                  onDelete={() => {
                    onDelete(i);
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Button variant="outline" size="sm" className="mt-2 self-start sm:mt-0" onClick={onAdd}>
            <Plus className="size-4" />
            {t('wizard.questions.addQuestion')}
          </Button>
        </div>
      )}
    </div>
  );
}
